import React, { useState, useEffect, useContext } from 'react';
import { AuthContext } from '../../App';

const TIPO_CONFIG = {
  feature: { label: 'Nueva función', color: 'bg-emerald-100 text-emerald-700', icon: 'fas fa-plus-circle' },
  fix: { label: 'Corrección', color: 'bg-red-100 text-red-700', icon: 'fas fa-bug' },
  improvement: { label: 'Mejora', color: 'bg-blue-100 text-blue-700', icon: 'fas fa-arrow-up' }
};

export default function HistorialCambios() {
  const { API } = useContext(AuthContext);
  const [changelog, setChangelog] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ version: '', titulo: '', fecha: new Date().toISOString().split('T')[0], cambios: [{ tipo: 'feature', descripcion: '' }] });
  const [saving, setSaving] = useState(false);

  const [seeding, setSeeding] = useState(false);

  const getHeaders = () => ({
    'Authorization': `Bearer ${localStorage.getItem('token')}`,
    'Content-Type': 'application/json'
  });

  useEffect(() => { loadChangelog(); }, []);

  const loadChangelog = async () => {
    try {
      // Primero migrar versiones nuevas que falten
      try {
        await fetch(`${API}/admin/changelog/migrate`, { method: 'POST', headers: getHeaders() });
      } catch (e) {}
      
      const res = await fetch(`${API}/admin/changelog`, { headers: getHeaders() });
      const data = await res.json();
      if (data.changelog?.length === 0) {
        // Intentar auto-seed
        try {
          const seedRes = await fetch(`${API}/admin/changelog/seed`, { method: 'POST', headers: getHeaders() });
          if (seedRes.ok) {
            const res2 = await fetch(`${API}/admin/changelog`, { headers: getHeaders() });
            const data2 = await res2.json();
            setChangelog(data2.changelog || []);
            return;
          }
        } catch (seedErr) {
          console.error('Auto-seed falló:', seedErr);
        }
      }
      setChangelog(data.changelog || []);
    } catch (e) {
      console.error('Error cargando changelog:', e);
    } finally {
      setLoading(false);
    }
  };

  const manualSeed = async () => {
    setSeeding(true);
    try {
      const res = await fetch(`${API}/admin/changelog/seed`, { method: 'POST', headers: getHeaders() });
      const data = await res.json();
      if (data.seeded) {
        await loadChangelog();
      } else if (data.message) {
        alert(data.message);
      }
    } catch (e) {
      console.error('Error en seed manual:', e);
    } finally {
      setSeeding(false);
    }
  };

  const addCambio = () => setForm(p => ({ ...p, cambios: [...p.cambios, { tipo: 'feature', descripcion: '' }] }));
  const removeCambio = (i) => setForm(p => ({ ...p, cambios: p.cambios.filter((_, idx) => idx !== i) }));
  const updateCambio = (i, field, val) => setForm(p => ({ ...p, cambios: p.cambios.map((c, idx) => idx === i ? { ...c, [field]: val } : c) }));

  const saveEntry = async () => {
    if (!form.version || !form.titulo || form.cambios.some(c => !c.descripcion)) return;
    setSaving(true);
    try {
      const res = await fetch(`${API}/admin/changelog`, {
        method: 'POST', headers: getHeaders(),
        body: JSON.stringify({ ...form, fecha: new Date(form.fecha + 'T12:00:00Z').toISOString() })
      });
      if (res.ok) {
        setShowForm(false);
        setForm({ version: '', titulo: '', fecha: new Date().toISOString().split('T')[0], cambios: [{ tipo: 'feature', descripcion: '' }] });
        loadChangelog();
      }
    } catch (e) {
      console.error('Error guardando:', e);
    } finally {
      setSaving(false);
    }
  };

  const deleteEntry = async (id) => {
    if (!window.confirm('¿Eliminar esta entrada del historial?')) return;
    try {
      await fetch(`${API}/admin/changelog/${id}`, { method: 'DELETE', headers: getHeaders() });
      setChangelog(prev => prev.filter(e => e.id !== id));
    } catch (e) {
      console.error('Error eliminando:', e);
    }
  };

  const formatDate = (dateStr) => {
    try {
      return new Date(dateStr).toLocaleDateString('es-UY', { year: 'numeric', month: 'long', day: 'numeric' });
    } catch { return dateStr; }
  };

  if (loading) return (
    <div className="flex items-center justify-center min-h-[400px]">
      <i className="fas fa-spinner fa-spin text-2xl text-blue-500 mr-3"></i>
      <span className="text-gray-500">Cargando historial...</span>
    </div>
  );

  return (
    <div className="max-w-4xl mx-auto p-4 sm:p-6" data-testid="historial-cambios-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between mb-8 gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-800">Historial de Cambios</h1>
          <p className="text-gray-500 mt-1">Registro de todas las actualizaciones del sistema</p>
        </div>
        <button
          onClick={() => setShowForm(!showForm)}
          className="flex items-center gap-2 px-4 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors shadow-sm"
          data-testid="add-changelog-btn"
        >
          <i className={`fas fa-${showForm ? 'times' : 'plus'}`}></i>
          {showForm ? 'Cancelar' : 'Nueva Versión'}
        </button>
      </div>

      {/* Formulario */}
      {showForm && (
        <div className="bg-white rounded-xl border-2 border-blue-200 p-5 mb-8 shadow-sm" data-testid="changelog-form">
          <h3 className="font-semibold text-gray-700 mb-4">Registrar nueva actualización</h3>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-4">
            <div>
              <label className="text-sm font-medium text-gray-600 mb-1 block">Versión</label>
              <input
                type="text" placeholder="ej: 2.6.0" value={form.version}
                onChange={e => setForm(p => ({ ...p, version: e.target.value }))}
                className="w-full border rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-300 focus:border-blue-400 outline-none"
                data-testid="changelog-version-input"
              />
            </div>
            <div>
              <label className="text-sm font-medium text-gray-600 mb-1 block">Título</label>
              <input
                type="text" placeholder="ej: Mejoras de rendimiento" value={form.titulo}
                onChange={e => setForm(p => ({ ...p, titulo: e.target.value }))}
                className="w-full border rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-300 focus:border-blue-400 outline-none"
                data-testid="changelog-titulo-input"
              />
            </div>
            <div>
              <label className="text-sm font-medium text-gray-600 mb-1 block">Fecha</label>
              <input
                type="date" value={form.fecha}
                onChange={e => setForm(p => ({ ...p, fecha: e.target.value }))}
                className="w-full border rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-300 focus:border-blue-400 outline-none"
                data-testid="changelog-fecha-input"
              />
            </div>
          </div>

          <label className="text-sm font-medium text-gray-600 mb-2 block">Cambios realizados</label>
          {form.cambios.map((cambio, i) => (
            <div key={i} className="flex items-center gap-2 mb-2">
              <select
                value={cambio.tipo}
                onChange={e => updateCambio(i, 'tipo', e.target.value)}
                className="border rounded-lg px-2 py-2 text-sm bg-white min-w-[130px]"
                data-testid={`cambio-tipo-${i}`}
              >
                <option value="feature">Nueva función</option>
                <option value="fix">Corrección</option>
                <option value="improvement">Mejora</option>
              </select>
              <input
                type="text" placeholder="Descripción del cambio"
                value={cambio.descripcion}
                onChange={e => updateCambio(i, 'descripcion', e.target.value)}
                className="flex-1 border rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-300 focus:border-blue-400 outline-none"
                data-testid={`cambio-desc-${i}`}
              />
              {form.cambios.length > 1 && (
                <button onClick={() => removeCambio(i)} className="text-red-400 hover:text-red-600 px-2">
                  <i className="fas fa-trash-alt"></i>
                </button>
              )}
            </div>
          ))}
          <button onClick={addCambio} className="text-sm text-blue-600 hover:text-blue-800 mt-1 mb-4">
            <i className="fas fa-plus mr-1"></i>Agregar otro cambio
          </button>

          <div className="flex justify-end">
            <button
              onClick={saveEntry} disabled={saving}
              className="px-5 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
              data-testid="save-changelog-btn"
            >
              {saving ? <><i className="fas fa-spinner fa-spin mr-2"></i>Guardando...</> : <><i className="fas fa-save mr-2"></i>Guardar</>}
            </button>
          </div>
        </div>
      )}

      {/* Timeline */}
      <div className="relative" data-testid="changelog-timeline">
        {/* Línea vertical */}
        <div className="absolute left-6 top-0 bottom-0 w-0.5 bg-gradient-to-b from-blue-300 via-blue-200 to-transparent hidden sm:block"></div>

        {changelog.length === 0 ? (
          <div className="text-center py-12 text-gray-400">
            <i className="fas fa-history text-4xl mb-3 block"></i>
            <p className="mb-4">No hay entradas en el historial</p>
            <button
              onClick={manualSeed}
              disabled={seeding}
              className="px-5 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
              data-testid="seed-changelog-btn"
            >
              {seeding ? <><i className="fas fa-spinner fa-spin mr-2"></i>Cargando...</> : <><i className="fas fa-download mr-2"></i>Cargar historial de versiones</>}
            </button>
          </div>
        ) : (
          changelog.map((entry, idx) => (
            <div key={entry.id} className="relative sm:pl-16 mb-6 group" data-testid={`changelog-entry-${entry.id}`}>
              {/* Punto en la timeline */}
              <div className={`absolute left-4 w-5 h-5 rounded-full border-3 hidden sm:flex items-center justify-center z-10 ${
                idx === 0 ? 'bg-blue-500 border-blue-300 shadow-lg shadow-blue-200' : 'bg-white border-blue-300'
              }`}>
                {idx === 0 && <span className="w-2 h-2 bg-white rounded-full"></span>}
              </div>

              <div className={`bg-white rounded-xl border transition-shadow hover:shadow-md ${
                idx === 0 ? 'border-blue-300 shadow-sm ring-1 ring-blue-100' : 'border-gray-200'
              }`}>
                {/* Header de la versión */}
                <div className={`flex flex-wrap items-center justify-between gap-3 px-5 py-3 rounded-t-xl ${
                  idx === 0 ? 'bg-blue-50' : 'bg-gray-50'
                }`}>
                  <div className="flex items-center gap-3 flex-wrap">
                    <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-bold ${
                      idx === 0 ? 'bg-blue-500 text-white' : 'bg-gray-200 text-gray-700'
                    }`} data-testid={`version-badge-${entry.id}`}>
                      v{entry.version}
                    </span>
                    <h3 className="font-semibold text-gray-800">{entry.titulo}</h3>
                    {idx === 0 && (
                      <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded-full font-medium">
                        Última versión
                      </span>
                    )}
                  </div>
                  <div className="flex items-center gap-3">
                    <span className="text-sm text-gray-500">
                      <i className="far fa-calendar-alt mr-1"></i>{formatDate(entry.fecha)}
                    </span>
                    <button
                      onClick={() => deleteEntry(entry.id)}
                      className="text-gray-300 hover:text-red-500 transition-colors opacity-0 group-hover:opacity-100"
                      title="Eliminar entrada"
                      data-testid={`delete-entry-${entry.id}`}
                    >
                      <i className="fas fa-trash-alt text-sm"></i>
                    </button>
                  </div>
                </div>

                {/* Lista de cambios */}
                <div className="px-5 py-3 space-y-2">
                  {(entry.cambios || []).map((cambio, ci) => {
                    const config = TIPO_CONFIG[cambio.tipo] || TIPO_CONFIG.feature;
                    return (
                      <div key={ci} className="flex items-start gap-3">
                        <span className={`inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-medium whitespace-nowrap mt-0.5 ${config.color}`}>
                          <i className={`${config.icon} text-[10px]`}></i>
                          {config.label}
                        </span>
                        <span className="text-sm text-gray-700">{cambio.descripcion}</span>
                      </div>
                    );
                  })}
                </div>

                {/* Footer */}
                {entry.creado_por && (
                  <div className="px-5 py-2 border-t border-gray-100 text-xs text-gray-400">
                    Registrado por {entry.creado_por}
                  </div>
                )}
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
}
